import { createSelector } from "reselect";
import { Map } from "immutable";

import { keyIn } from "@util/index";
import {
    AuthState as State,
    User,
    Organisation,
    Role,
    Permission,
} from "../models";

type PermissionAction = "view" | "create" | "update" | "delete" | "track" | "generate";

export interface PropsWithId {
    params: {
        id: string;
    };
}

export interface PropsWithName {
    params: {
        name: string;
    };
}

// The entity maps.
export const getEntities = (state: State) => state.auth.entities;
export const getUsersMap = (state: State) => state.auth.entities.users;
export const getOrganisationsMap = (state: State) => state.auth.entities.organisations;
export const getRolesMap = (state: State) => state.auth.entities.roles;
export const getPermissionsMap = (state: State) => state.auth.entities.permissions;

// The entity arrays.
export const getUsers = createSelector(
    getUsersMap,
    (items: Map<number, User>) => items.toArray(),
);
export const getOrganisations = createSelector(
    getOrganisationsMap,
    (items: Map<number, Organisation>) => items.toArray(),
);
export const getRoles = createSelector(
    getRolesMap,
    (items: Map<string, Role>) => items.toArray(),
);
export const getPermissions = createSelector(
    getPermissionsMap,
    (items: Map<number, Permission>) => items.toArray(),
);

// Get individual entities
const getUser = (state: State, props: PropsWithId) =>
    state.auth.entities.users.get(Number(props.params.id));
const getOrganisation = (state: State, props: PropsWithId) =>
    state.auth.entities.organisations.get(Number(props.params.id));
const getRole = (state: State, props: PropsWithName) =>
    state.auth.entities.roles.get(props.params.name);
const getPermission = (state: State, props: PropsWithId) =>
    state.auth.entities.permissions.get(Number(props.params.id));

export const makeGetUser = () => createSelector(
    getUser,
    (item: User) => item,
);
export const makeGetOrganisation = () => createSelector(
    getOrganisation,
    (item: Organisation) => item,
);
export const makeGetRole = () => createSelector(
    getRole,
    (item: Role) => item,
);
export const makeGetPermission = () => createSelector(
    getPermission,
    (item: Permission) => item,
);

// The entity map sizes.
export const getNumUsers = createSelector(
    getUsersMap,
    (items: Map<number, User>) => items.size,
);
export const getNumOrganisations = createSelector(
    getOrganisationsMap,
    (items: Map<number, Organisation>) => items.size,
);
export const getNumRoles = createSelector(
    getRolesMap,
    (items: Map<string, Role>) => items.size,
);
export const getNumPermissions = createSelector(
    getPermissionsMap,
    (items: Map<number, Permission>) => items.size,
);

// Logged in data.
export const getIsAuthenticated = (state: State) => state.auth.authenticated;
export const getAccessToken = (state: State) => state.auth.accessToken;
export const getCurrentUserId = (state: State) => state.auth.user;
export const getCurrentUser = createSelector(
    getUsersMap,
    getCurrentUserId,
    (users: Map<number, User>, userId: number) => {
        if (userId != null && users.has(userId)) {
            return users.get(userId);
        }
        return null;
    },
);
export const getCurrentOrganisation = createSelector(
    getCurrentUser,
    getOrganisationsMap,
    (user: User, organisations: Map<number, Organisation>) => {
        if (user != null && organisations.has(user.organisation)) {
            return organisations.get(user.organisation);
        }
        return null;
    },
);
export const getCurrentRoles = createSelector(
    getCurrentUser,
    getRolesMap,
    (user: User, roles: Map<string, Role>) => {
        if (user != null) {
            return roles.filter(keyIn(user.roles)).toArray();
        }
        return [];
    },
);
export const getCurrentPermissions = createSelector(
    getCurrentRoles,
    getPermissionsMap,
    (roles: Role[], permissions: Map<number, Permission>) => {
        let currentPermissions = Map<number, Permission>();
        for (const role of roles) {
            currentPermissions = currentPermissions.merge(
                permissions.filter(keyIn(role.permissions)),
            );
        }
        return currentPermissions.toArray();
    },
);
export const getLesserEqualRoles = createSelector(
    getCurrentRoles,
    getRoles,
    (currentRoles: Role[], allRoles: Role[]) => {
        // Get the lowest level for the current user.
        let userLevel = 100;
        for (const role of currentRoles) {
            if (role.level < userLevel) {
                userLevel = role.level;
            }
        }
        return allRoles.filter((role: Role) => role.level >= userLevel);
    },
);

// Permissions - factories to create the permission selectors
export const canPermissionSelectorFactory = (prefix: string, action: PermissionAction) => {
    return createSelector(
        getCurrentPermissions,
        (permissions: Permission[]) => {
            const permissionName = prefix + "." + action;
            return permissions.filter(
                (permission: Permission) => permission.name === permissionName,
            ).length > 0;
        },
    );
};
export const levelPermissionSelectorFactory = (prefix: string, action: PermissionAction) => {
    return createSelector(
        getCurrentUser,
        getCurrentPermissions,
        (user: User, permissions: Permission[]) => {
            const permissionName = prefix + "." + action;
            const matches = permissions.filter(
                (permission: Permission) => permission.name === permissionName,
            );
            if (matches.length > 0 && user.permissionsLevels != null
                && user.permissionsLevels[matches[0].id] !== undefined) {
                return user.permissionsLevels[matches[0].id];
            }
            return null;
        },
    );
};

const userPermissionPrefix = "wata.auth.user";
export const canViewUser = canPermissionSelectorFactory(userPermissionPrefix, "view");
export const canCreateUser = canPermissionSelectorFactory(userPermissionPrefix, "create");
export const canUpdateUser = canPermissionSelectorFactory(userPermissionPrefix, "update");
export const canDeleteUser = canPermissionSelectorFactory(userPermissionPrefix, "delete");
export const getViewUserLevel = levelPermissionSelectorFactory(userPermissionPrefix, "view");
export const getCreateUserLevel = levelPermissionSelectorFactory(userPermissionPrefix, "create");
export const getUpdateUserLevel = levelPermissionSelectorFactory(userPermissionPrefix, "update");
export const getDeleteUserLevel = levelPermissionSelectorFactory(userPermissionPrefix, "delete");
